/**
 * Runs a set of async tasks with bounded parallelism.
 *
 * A pool of at most `concurrency` workers pulls tasks off the input
 * array in order and awaits each one. Individual task failures are
 * swallowed — the caller is expected to capture success / failure
 * inside the task itself (via a shared counter, a Set, etc.) — so a
 * single failure never aborts the batch.
 *
 * If `concurrency` is greater than `tasks.length`, only `tasks.length`
 * workers are spawned. If `tasks` is empty, resolves immediately.
 */
export async function runWithConcurrency<T>(
  tasks: ReadonlyArray<() => Promise<T>>,
  concurrency: number
): Promise<void> {
  if (tasks.length === 0) {
    return;
  }
  let cursor = 0;
  const workerCount = Math.max(1, Math.min(concurrency, tasks.length));
  const workers = Array.from({ length: workerCount }, async () => {
    while (cursor < tasks.length) {
      const idx = cursor++;
      try {
        await tasks[idx]();
      } catch {
        /* individual failures are captured inside the task */
      }
    }
  });
  await Promise.all(workers);
}
